import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { TrashIcon, WebhookIcon } from 'lucide-react';
import { FaExclamationTriangle } from 'react-icons/fa';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DataTableColumnHeader } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTableColumnHeader';
import { validateJwtClaims } from '@/utils/integrationToken';
import { CopyTokenButton } from './CopyTokenButton';

/** One added integration of the store, with its resolved title and token. */
export type IntegrationRow = {
  service: string;
  name: string;
  isCustom: boolean;
  createdAt: string;
  token: string | undefined;
};

/** The integration name, with a warning icon when the minted JWT misses claims. */
function renderNameCell(row: IntegrationRow) {
  const isTokenValid = !row.token || validateJwtClaims(row.token).isValid;

  return (
    <div className="tw:flex tw:items-center tw:gap-2">
      <span>{row.name}</span>
      {!isTokenValid && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>
              <FaExclamationTriangle className="tw:text-destructive" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="tw:max-w-xs">
            <strong>{i18n.t('Integrations:jwtValidationWarning')}</strong>
            <br />
            {i18n.t('Integrations:jwtValidationWarningMessage')}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export type GetIntegrationColumnsOptions = {
  /** Opens the confirm flow for removing the integration. */
  onRemove: (service: string, name: string) => void;
  /**
   * Builds the link to the "Simulate webhooks" page for a row. Only provided
   * in dev environments (devFeaturesEnabled) — the action is hidden otherwise.
   */
  getSimulateWebhooksHref?: (row: IntegrationRow) => string;
};

export function getIntegrationColumns({
  onRemove,
  getSimulateWebhooksHref,
}: GetIntegrationColumnsOptions): ColumnDef<IntegrationRow>[] {
  return [
    {
      accessorKey: 'name',
      // The row is meaningless without its name — not hideable via view options
      enableHiding: false,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('Integrations:integration')}
        />
      ),
      cell: ({ row }) => renderNameCell(row.original),
      meta: {
        getName: () => i18n.t('Integrations:integration'),
      },
    },
    {
      accessorKey: 'isCustom',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('Integrations:type')}
        />
      ),
      cell: ({ row }) =>
        row.original.isCustom ? (
          <Badge>{i18n.t('Integrations:typeCustom')}</Badge>
        ) : (
          <Badge variant="secondary">
            {i18n.t('Integrations:typePredefined')}
          </Badge>
        ),
      meta: {
        getName: () => i18n.t('Integrations:type'),
      },
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('Integrations:createdAt')}
        />
      ),
      cell: ({ row }) => (
        <span title={row.original.createdAt}>
          {new Date(row.original.createdAt).toLocaleDateString(
            i18n.language,
            getDateFormatOptions(true),
          )}
        </span>
      ),
      meta: {
        getName: () => i18n.t('Integrations:createdAt'),
      },
    },
    {
      accessorKey: 'token',
      enableSorting: false,
      header: () => <div>{i18n.t('Integrations:token')}</div>,
      cell: ({ row }) =>
        row.original.token ? (
          <span className="tw:font-mono tw:text-xs tw:max-w-[260px] tw:truncate tw:block">
            {row.original.token}
          </span>
        ) : (
          '-'
        ),
      meta: {
        getName: () => i18n.t('Integrations:token'),
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      header: () => <div>{i18n.t('Integrations:actions')}</div>,
      cell: ({ row }) => (
        <div className="tw:flex tw:gap-2">
          <CopyTokenButton token={row.original.token} />
          {getSimulateWebhooksHref && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" asChild>
                  <Link to={getSimulateWebhooksHref(row.original)}>
                    <WebhookIcon className="tw:size-4" />
                    <span className="tw:sr-only">
                      {i18n.t('Integrations:simulateWebhooks')}
                    </span>
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {i18n.t('Integrations:simulateWebhooks')}
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() =>
                  onRemove(row.original.service, row.original.name)
                }
              >
                <TrashIcon className="tw:size-4" />
                <span className="tw:sr-only">
                  {i18n.t('Integrations:removeIntegration')}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {i18n.t('Integrations:removeIntegration')}
            </TooltipContent>
          </Tooltip>
        </div>
      ),
    },
  ];
}
